import { EditOutlined, LockOutlined, LogoutOutlined } from "@ant-design/icons";
import { useLogout } from "admin/auth/use_logout";
import { changePassword } from "admin/rest_api";
import { Alert, App, Button, Col, Form, Input, Row, Space } from "antd";
import features from "features";
import Toast from "libs/toast";
import messages from "messages";
import { useState } from "react";
import { SettingsTitle } from "./helpers/settings_title";

const FormItem = Form.Item;
const { Password } = Input;

import PasskeysView from "../auth/passkeys_view";
import { SettingsCard, type SettingsCardProps } from "./helpers/settings_card";

const MIN_PASSWORD_LENGTH = 8;

function AccountSecurityView() {
  const [form] = Form.useForm();
  const { modal } = App.useApp();
  const logout = useLogout();

  const [isResetPasswordVisible, setResetPasswordVisible] = useState(false);

  async function onFinish(formValues: Record<string, any>) {
    try {
      await changePassword(formValues);
      Toast.success(messages["auth.reset_pw_confirmation"]);
      await logout({ everywhere: true });
    } catch (error) {
      console.error("Password change failed:", error);
      Toast.error("Failed to change password. Please try again.");
    }
  }

  function checkPasswordsAreMatching(value: string, otherPasswordFieldKey: string[]) {
    const otherFieldValue = form.getFieldValue(otherPasswordFieldKey);

    if (value && otherFieldValue) {
      if (value !== otherFieldValue) {
        return Promise.reject(new Error(messages["auth.registration_password_mismatch"]));
      } else if (form.getFieldError(otherPasswordFieldKey).length > 0) {
        // If the other password field still has errors, revalidate it.
        form.validateFields([otherPasswordFieldKey]);
      }
    }

    return Promise.resolve();
  }

  function getPasswordComponent() {
    return isResetPasswordVisible ? (
      <Form onFinish={onFinish} form={form}>
        <FormItem
          name="oldPassword"
          rules={[
            {
              required: true,
              message: messages["auth.reset_old_password"],
            },
          ]}
        >
          <Password
            prefix={
              <LockOutlined
                style={{
                  fontSize: 13,
                }}
              />
            }
            placeholder="Old Password"
          />
        </FormItem>
        <FormItem
          hasFeedback
          name={["password", "password1"]}
          rules={[
            {
              required: true,
              message: messages["auth.reset_new_password"],
            },
            {
              min: MIN_PASSWORD_LENGTH,
              message: messages["auth.registration_password_length"],
            },
            {
              validator: (_, value: string) =>
                checkPasswordsAreMatching(value, ["password", "password2"]),
            },
          ]}
        >
          <Password
            prefix={
              <LockOutlined
                style={{
                  fontSize: 13,
                }}
              />
            }
            placeholder="New Password"
          />
        </FormItem>
        <FormItem
          hasFeedback
          name={["password", "password2"]}
          rules={[
            {
              required: true,
              message: messages["auth.reset_new_password2"],
            },
            {
              min: MIN_PASSWORD_LENGTH,
              message: messages["auth.registration_password_length"],
            },
            {
              validator: (_, value: string) =>
                checkPasswordsAreMatching(value, ["password", "password1"]),
            },
          ]}
        >
          <Password
            prefix={
              <LockOutlined
                style={{
                  fontSize: 13,
                }}
              />
            }
            placeholder="Confirm New Password"
          />
        </FormItem>
        <Alert
          type="info"
          title={messages["auth.reset_logout"]}
          showIcon
          style={{
            marginBottom: 24,
          }}
        />
        <FormItem>
          <Space>
            <Button onClick={() => setResetPasswordVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Update Password
            </Button>
          </Space>
        </FormItem>
      </Form>
    ) : null;
  }

  function handleResetPassword() {
    setResetPasswordVisible(!isResetPasswordVisible);
  }

  async function handleLogout() {
    logout({ everywhere: true }).catch((error) => {
      Toast.error("Failed to log out. See console for more details");
      console.error("Logout failed:", error);
    });
  }

  const handleLogoutEverywhere = () => {
    modal.confirm({
      title: "Confirm Logout",
      content: <p>Are you sure you want to log out on all devices?</p>,
      onOk: handleLogout,
    });
  };
  const securityItems: SettingsCardProps[] = [
    {
      title: "Password",
      icon: <LockOutlined />,
      // While editing, the form replaces the value and already explains the logout.
      content: isResetPasswordVisible ? null : "•••••••••••",
      hint: isResetPasswordVisible ? null : "Changing it logs you out on all devices",
      children: getPasswordComponent(),
      action: (
        <Button
          type="default"
          shape="circle"
          icon={<EditOutlined />}
          size="small"
          onClick={handleResetPassword}
        />
      ),
    },
    {
      title: "Log out everywhere",
      icon: <LogoutOutlined />,
      content: (
        <Button type="default" onClick={handleLogoutEverywhere}>
          Log out on all devices
        </Button>
      ),
    },
  ];

  const { passkeysEnabled } = features();

  return (
    <div>
      <SettingsTitle
        title="Security"
        description="Manage your password and logins across devices"
      />
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {securityItems.map((item) => (
          <Col span={12} key={item.title}>
            <SettingsCard {...item} />
          </Col>
        ))}
      </Row>

      {passkeysEnabled && (
        <>
          <SettingsTitle title="Passkeys" description="Login passwordless with Passkeys" />
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <SettingsCard title="Passkeys" content={<PasskeysView />} />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default AccountSecurityView;
